<template>
  <v-btn icon class='subtitle-btn' @click='handleOpen(true)'>
    <span v-html="icon"></span>
    <Readonly 
      :closeable='true'
      :open="open" 
      @input='handleOpen'
      :text="text" 
      :html="html"
      :display-text="displayText || ''" 
      :subtext="subtext"
      :playing="playing"
      :started="started"
      :display-mode="displayMode" 
      :language="language" 
      :typingAnimation='typingAnimation'
      :initialSpeed="speed"
      :voices="voices"
      :voice="voice"
      :ttsMode="ttsMode"
      :speed="speed"
      @display-mode-changed="handleDisplayMode"
      @play-pause="handlePlayPause"
      @tts-mode-changed="handleTTSMode"
      @voice-changed="handleVoiceChanged"
      @speed-changed="handleSpeedChange"
      transition='dialog-bottom-transition'
    >
      <template #maintext>
        <div v-html="html"></div>
      </template>
      <template #subtext>
        <div v-html="htmlSub"></div>
      </template>
    </Readonly>
  </v-btn>
</template>


<script>
import { inject, computed, onMounted, ref, watch} from 'vue'
import Readonly from '../readonly/Readonly.vue';
import { inflateTemplate } from '../../utils/wordParsing';
import { useEvent } from '../../plugins/vue-gtag';
import { setupReadonly } from '../../hooks/readonly';
import IpaText from '../IpaText.vue';

export default {
  components: { Readonly, IpaText },
  setup(){
    const html = ref(null);
    const htmlSub = ref(null);
    const open = inject('subtitleOpen');
    const gaSubtitleMode = useEvent('subtitle_mode');

    const language = inject('language');
    const text = inject('appTTS');
    const ipaText = inject('ipaText');
    const speed = inject('speed');
    const ttsMode = inject('ttsMode');
    const ttsIndex = inject('ttsIndex');
    const ttsLocale = inject('ttsLocale');
    const playing = inject('playing');
    const started = inject('started')
    const ttsManagerService = inject('ttsManagerService');
    const core = inject('core');
    const voice = inject('voice');
    const voices = inject('voices');
    const voiceId = inject('voiceId');
    const useIpa = inject('useIpa');
    const plainWords = inject('plainWords');

    const inflateTemplateFn = (markIndex, textId, speed, showIpa) => {
      const audioMap = textId && core?.value?.audiosMaps ? core?.value?.audiosMaps[textId] : null;
      const allHtml = inflateTemplate(text.value, markIndex, ttsMode.value, ipaText.value, core?.value?.currentTime?.value, audioMap, speed, showIpa);
      const query = `[data-index="${markIndex}"]`;
      const doc = new DOMParser().parseFromString(`<div>${allHtml}<div>`, "text/xml");
      const element = doc.querySelectorAll(query);
      return element?.[0]?.innerHTML;
    };

    const {
      displayText,
      subtext,
      handleTTSMode,
      handleSpeedChange,
      handleDisplayMode,
      handlePlayPause,
      handleVoiceChanged,
      currentIpaChunk,
      currentInputChunk,
      displayMode,
      typingAnimation
    } = setupReadonly({
      text,
      ipaText,
      playing,
      started,
      ttsIndex,
      ttsLocale,
      ttsMode,
      speed,
      ttsManagerService,
      voice,
    })

    const refreshHtml = () => {
      if(!plainWords.value){
        return;
      }
      let trackingIndex = ttsIndex.value;
      const word = plainWords.value[trackingIndex];
      const textId = word.trim() + speed.value + voiceId.value + (useIpa.value ? 'ipa' : '');
      html.value = inflateTemplateFn(ttsIndex.value, textId, speed.value, displayMode.value !== 'ipa');
      htmlSub.value = inflateTemplateFn(ttsIndex.value, textId, speed.value, displayMode.value === 'ipa');
    }

    watch(open, (newVal) => {
      if(newVal){
        displayMode.value = 'ipa';
      }
      refreshHtml();
    });
    watch(ipaText, (_) => {
      refreshHtml();
    });
    watch(displayMode, (_) => {
      refreshHtml();
    });

    watch(core, (newValue) => {
      newValue.addEventListener('timeupdate', () => {
        // console.log('timeupdate subtitle button');
        if(open.value){
          let trackingIndex = ttsIndex.value;
          const word = plainWords.value[trackingIndex];
          const textId = word.trim() + speed.value + voiceId.value + (useIpa.value ? 'ipa' : '');
          // console.log(textId);
          html.value = inflateTemplateFn(ttsIndex.value, textId, speed.value, displayMode.value !== 'ipa');
          htmlSub.value = inflateTemplateFn(ttsIndex.value, textId, speed.value, displayMode.value === 'ipa');
        }
      });
    });

    const icon = computed(function(){
      return open.value 
        ? '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" aria-hidden="true" focusable="false" width="1em" height="1em" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24" data-icon="mdi-closed-caption" class="iconify" style="vertical-align: -0.125em; transform: rotate(360deg);"><path d="M18 11h-1.5v-.5h-2v3h2V13H18v1a1 1 0 0 1-1 1h-3a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1m-7 1H9.5v-.5h-2v3h2V13H11v1a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1m8-6H5c-1.11 0-2 .89-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" fill="currentColor"></path></svg>'
        : '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" aria-hidden="true" focusable="false" width="1em" height="1em" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24" data-icon="mdi-closed-caption-outline" class="iconify" style="vertical-align: -0.125em; transform: rotate(360deg);"><path d="M5 4c-.55 0-1 .18-1.41.57C3.2 4.96 3 5.44 3 6v12c0 .56.2 1.04.59 1.43c.41.39.86.57 1.41.57h14c.5 0 1-.19 1.39-.59c.41-.41.61-.88.61-1.41V6c0-.53-.2-1-.61-1.41C20 4.19 19.5 4 19 4H5m-.5 1.5h15v13h-15v-13M7 9c-.3 0-.53.09-.72.28c-.19.19-.28.42-.28.72v4c0 .3.09.53.28.72c.19.19.42.28.72.28h3c.27 0 .5-.09.71-.28c.2-.19.29-.42.29-.72v-1H9.5v.5h-2v-3h2v.5H11v-1c0-.3-.09-.53-.29-.72C10.5 9.09 10.27 9 10 9H7m7 0c-.27 0-.5.09-.71.28c-.2.19-.29.42-.29.72v4c0 .3.09.53.29.72c.21.19.44.28.71.28h3c.3 0 .53-.09.72-.28c.19-.19.28-.42.28-.72v-1h-1.5v.5h-2v-3h2v.5H18v-1c0-.3-.09-.53-.28-.72C17.53 9.09 17.3 9 17 9h-3z" fill="currentColor"></path></svg>';
    })


    const handleOpen = (newVal)=>{
      open.value = newVal;

      gaSubtitleMode(newVal);
    }

    return {
      html,
      htmlSub,
      icon,
      handleOpen,
      open,
      text,
      playing,
      started,
      speed,
      displayText,
      subtext,
      displayMode,
      typingAnimation,
      voices,
      voice,
      ttsMode,
      handleTTSMode,
      handleDisplayMode,
      handleSpeedChange,
      handlePlayPause,
      handleVoiceChanged,
      language,
      currentIpaChunk,
      currentInputChunk
    }
  }
};
</script>
